import React from 'react'
import propTypes from 'prop-types'
import moment from 'moment'
import { useQuery, useClient } from 'jsonapi-react'
import { FaPlay } from 'react-icons/fa'
import filter from 'lodash/filter'
import find from 'lodash/find'
import map from 'lodash/map'
import pickBy from 'lodash/pickBy'
import isEmpty from 'lodash/isEmpty'
import findIndex from 'lodash/findIndex'
import { useInterval } from 'react-use'

import Input, { InputFile } from 'components/Forms/Input'
import Tab from 'components/Tab'
import Grid from 'components/Grid'
import AlbumItemThumb from 'components/Albums/Item/AlbumItemThumb'
import Button from 'components/Button'
import Datetime from 'components/Forms/Datetime'
import Popover from 'components/Popover'
import Listbox from 'components/Listbox'
import DropUpload from 'components/DropUpload'
import { uploadFiles } from 'helpers'


export default function Files(props) {
  const [isUploadingBulk, setIsUploadingBulk] = React.useState(false)
  const [filesPage, setFilesPage] = React.useState(1)
  const [selectedFiles, setSelectedFiles] = React.useState([])
  const [filterFiles, setFilterFiles] = React.useState({})

  const client = useClient()

  const {
    data: files = {},
    meta: filesMeta = {},
    refetch: filesRefetch,
    isLoading: isLoadingFiles = true
  } = useQuery([
    'users',
    'me',
    'files',
    {
      'page[number]': filesPage,
      'page[size]': 30,
      ...filterFiles,
      'sort': '-created_at',
    },
  ])

  const {
    data: albums = {},
    isLoading: isLoadingAlbums = true
  } = useQuery([
    'users',
    'me',
    'albums',
    {
      'page[size]': 999999999,
      'fields[Album]': 'title,id',
    },
  ])

  useInterval(
    () => {
      filesRefetch()
    },
    5000,
  )

  const _uploadFiles = async (e, files=null) => {
    setIsUploadingBulk(true)

    if ( ! files) {
      files = e.currentTarget.files
    }
    const _files = await uploadFiles(files, client)
    setSelectedFiles(oldFiles => [...oldFiles, ..._files])

    setIsUploadingBulk(false)
  }

  const albumsOptions = [
    {
      label: 'All',
      value: 'all',
    },
    {
      label: 'Files not linked',
      value: '0',
    },
    ...map(
      albums,
      album => ({
        label: album.title,
        value: album.id,
      })
    )
  ]
  const albumSelected = find(albumsOptions, { value: filterFiles['filter[used_in_albums]'] }) || null

  return (
    <DropUpload uploadedFiles={files => setSelectedFiles(oldFiles => [...oldFiles, ...files])}>
      <div className='prose-sm lg:prose-lg'>
        <h2>{props.title}</h2>
        <div></div>
      </div>
      <Tab className='mb-5'>
        <Tab.Item
          onChange={
            () => {
              setFilesPage(1)
              setFilterFiles(filters => pickBy(
                filters,
                (_filters, key) => key !== 'filter[type]',
              ))
            }
          }
          selected={filterFiles['filter[type]'] === undefined}
        >
          All
        </Tab.Item>
        <Tab.Item
          onChange={
            () => {
              setFilesPage(1)
              setFilterFiles(filters => ({
                ...filters,
                'filter[type]': 'Image',
              }))
            }
          }
          selected={filterFiles['filter[type]'] === 'Image'}
        >
          Images
        </Tab.Item>
        <Tab.Item
          onChange={
            () => {
              setFilesPage(1)
              setFilterFiles(filters => ({
                ...filters,
                'filter[type]': 'Video',
              }))
            }
          }
          selected={filterFiles['filter[type]'] === 'Video'}
        >
          Videos
        </Tab.Item>
        <Tab.Item
          onChange={
            () => {
              setFilesPage(1)
              setFilterFiles(filters => ({
                ...filters,
                'filter[type]': 'Audio',
              }))
            }
          }
          selected={filterFiles['filter[type]'] === 'Audio'}
        >
          Audios
        </Tab.Item>
      </Tab>
      <div className='flex space-x-5 flex-col lg:flex-row'>
        <div className='max-w-full lg:max-w-lg'>
          <InputFile
            label='Upload multiple files'
            inputProps={{
              multiple: true,
              accept: 'image/jpg, image/png, image/jpeg, video/*, audio/*',
            }}
            className='mb-10 w-full text-center'
            onChange={_uploadFiles}
            loading={isUploadingBulk}
          />
          <div className='bg-disabled-100 p-5 mb-5'>
            <form
              onSubmit={e => e.preventDefault()}
              onReset={() => setFilterFiles({})}
            >
              <Input
                timeoutms={400}
                onChange={e => {
                  const value = e.target.value
                  if (value !== '') {
                    setFilterFiles(
                      filters => ({
                        ...filters,
                        'filter[search]': e.target.value,
                      })
                    )
                  } else {
                    setFilterFiles(
                      filters => pickBy(filters, (_, key) => key !== 'filter[search]')
                    )
                  }
                }}
                inputProps={{
                  placeholder: 'Search...',
                }}
              />
              <Datetime
                type='date'
                clearable
                startDatetime={filterFiles['filter[created_at__gt]'] && moment(filterFiles['filter[created_at__gt]'])}
                endDatetime={filterFiles['filter[created_at__lt]'] && moment(filterFiles['filter[created_at__lt]'])}
                onChange={
                  ({ start, end }) => setFilterFiles(
                    filters => ({
                      ...filters,
                      'filter[created_at__gt]': start?.format(),
                      'filter[created_at__lt]': end?.format(),
                    })
                  )
                }
              />
              <label className='mb-10 block'>
                <div className='mb-2'>Used in album</div>
                <Listbox
                  defaultValue={albumsOptions[0]}
                  value={albumSelected}
                  onChange={
                    selected => {
                      if (selected.value === 'all') {
                        setFilterFiles(
                          filters => pickBy(filters, (_, key) => key !== 'filter[used_in_albums]')
                        )

                      } else {
                        setFilterFiles(
                          filters => ({
                            ...filters,
                            'filter[used_in_albums]': selected.value,
                          })
                        )

                      }
                    }
                  }
                  loading={isLoadingAlbums}
                  options={albumsOptions}
                />
              </label>
              <Button
                disabled={isEmpty(filterFiles)}
                theme={isEmpty(filterFiles) ? 'disabled' : 'default'}
                className='inverted'
                type='reset'
              >
                Reset
              </Button>
            </form>
          </div>
        </div>
        <div className='flex-grow'>
          <Grid
            loading={isLoadingFiles}
            pagination={{
              selected: filesPage,
              pages: filesMeta?.pagination?.pages || 0,
              onChange: setFilesPage,
            }}
          >
            {
              map(
                files,
                file => (
                  <Grid.Item
                    key={`file_${file.id}`}
                    canSelect={true}
                    selected={!!find(selectedFiles, {id: file.id})}
                    indexSelected={findIndex(selectedFiles, {id: file.id})}
                    onChange={() => {
                      if (find(selectedFiles, { id: file.id })) {
                        setSelectedFiles(
                          oldFiles => filter(
                            oldFiles,
                            oldFile => oldFile.id !== file.id,
                          )
                        )
                        return
                      }
                      setSelectedFiles(oldFiles => ([
                        ...oldFiles,
                        file,
                      ]))
                    }}
                  >
                    <AlbumItemThumb
                      file={file}
                    />
                    {
                      (file.jobStatus === 'completed' || file.jobStatus === 'ok') && (
                        (file.type === 'Audio' || file.type === 'Video') &&
                        <a
                          href={file.file}
                          target='blank'
                          className='
                            absolute left-1 bottom-1 p-2 z-20
                            rounded-full bg-primary-600 bg-opacity-50 text-white
                            transition-all
                            flex items-center justify-center
                            hover:bg-primary-800
                          '
                        >
                          <FaPlay />
                        </a>
                      )
                    }
                  </Grid.Item>
                )
              )
            }
          </Grid>
        </div>
      </div>
      {
        selectedFiles.length > 0 &&
        <div className='sticky bg-white bottom-0 py-5 z-20 border-t mt-10'>
          <div>{selectedFiles.length} files selected</div>
          <div className='flex space-x-3'>
            {
              (
                ! find(selectedFiles, { jobStatus: 'error' }) &&
                ! find(selectedFiles, { jobStatus: 'pending' }) &&
                ! find(selectedFiles, { jobStatus: 'processing' })
              ) &&
              <Button onClick={() => props.onSubmit(selectedFiles)} className='inverted'>
                {props.labelSubmit}
              </Button>
            }
            <Button className='inverted disabled' onClick={() => setSelectedFiles([])}>Cancel</Button>
            <div className='flex-grow' />
            <Popover
              position='right'
              panelClassName='ring-1 ring-danger-600'
              handler={
                <Button
                  className='inverted'
                  theme='danger'
                >
                  Delete
                </Button>
              }
            >
              This action is not reversible.
              <Button
                onClick={
                  async () => {
                    await Promise.all(
                      map(
                        selectedFiles,
                        async selectedFile => await client.delete([
                          'files',
                          selectedFile.id,
                        ])
                      )
                    )
                    setSelectedFiles([])
                  }
                }
                theme='danger'
                className='w-full inverted mt-3'
              >
                Delete
              </Button>
            </Popover>
          </div>
        </div>
      }
    </DropUpload>
  )
}


Files.propTypes = {
  onSubmit: propTypes.func.isRequired,
  labelSubmit: propTypes.string,
  title: propTypes.string,
}

Files.defaultProps = {
  labelSubmit: 'Submit',
  title: 'Library',
}
